import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { reaction } from 'mobx';

import { BanksApi } from 'api/BanksApi';
import { getFiBankStore } from 'stores/FiBankStore';
import { getFiRelationshipManagerStore } from 'stores/FiRelationshipManagerStore';
import { ACCOUNT_OWNER_METADATA_KEY } from 'tanstack-hooks/keys';
import { useUser } from 'tanstack-hooks/user';

export function useAccountOwnerMetadata({
  shouldNotifyOnChange = true
}: { shouldNotifyOnChange?: boolean } = {}) {
  const banksApi = new BanksApi();
  const relationshipManagerStore = getFiRelationshipManagerStore();
  const { userBankId } = useUser();
  const bankStore = getFiBankStore();

  const { data: accountOwnerMetadata, isLoading: isLoadingAccountOwnerMetadata } = useQuery({
    enabled: !!userBankId.length,
    queryKey: [ACCOUNT_OWNER_METADATA_KEY, userBankId],
    queryFn: () => banksApi.getAccountOwnerMetadata(userBankId),
    ...(!shouldNotifyOnChange && { notifyOnChangeProps: [] })
  });

  // set initial metadata when available
  useEffect(() => {
    if (!isEmpty(accountOwnerMetadata))
      relationshipManagerStore.setAccountOwnerMetadata(accountOwnerMetadata);
  }, [accountOwnerMetadata]);

  // wait until bank data is loaded, then set relationship managers
  useEffect(() => {
    const dispose = reaction(
      () => ({
        staffLength: bankStore?.staff?.length || 0,
        groupsLength: bankStore.bank.groups?.length || 0,
        metadata: accountOwnerMetadata
      }),
      ({ staffLength, groupsLength, metadata }) => {
        if (metadata && staffLength > 0 && groupsLength > 0) {
          relationshipManagerStore.setRelationshipManagers(metadata);
        }
      }
    );

    return dispose;
  }, [accountOwnerMetadata]);

  return { accountOwnerMetadata, isLoadingAccountOwnerMetadata };
}
